import { Component } from '@theme/component';
import { onAnimationEnd, isBackForwardNavigation } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

let cartSyncInFlight = null;
let lastCartSyncAt = 0;
const CART_SYNC_DEDUP_MS = 500;
const cartIconInstances = new Set();
let isPageShowListenerBound = false;

const syncCartFromServer = async () => {
  if (cartSyncInFlight) return cartSyncInFlight;

  if (Date.now() - lastCartSyncAt < CART_SYNC_DEDUP_MS) return null;

  cartSyncInFlight = (async () => {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to sync cart (${response.status})`);
      }

      const cart = await response.json();
      const itemCount = Number.isFinite(cart?.item_count) ? cart.item_count : 0;

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-icon', {
          itemCount,
          source: 'cart-icon-pageshow',
        })
      );
    } catch (error) {
      console.error('Failed to sync cart after browser back navigation:', error);
    } finally {
      lastCartSyncAt = Date.now();
      cartSyncInFlight = null;
    }
  })();

  return cartSyncInFlight;
};

/** @param {PageTransitionEvent} event */
const handleGlobalPageShow = (event) => {
  if (!event.persisted && !isBackForwardNavigation()) return;

  for (const cartIcon of cartIconInstances) {
    cartIcon.ensureCartBubbleIsCorrect();
  }

  syncCartFromServer();
};

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);

    cartIconInstances.add(this);
    if (!isPageShowListenerBound) {
      window.addEventListener('pageshow', handleGlobalPageShow);
      isPageShowListenerBound = true;
    }

    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);

    cartIconInstances.delete(this);
    if (cartIconInstances.size === 0 && isPageShowListenerBound) {
      window.removeEventListener('pageshow', handleGlobalPageShow);
      isPageShowListenerBound = false;
    }
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // Network-failure CartAddEvents still clear drawer pending chrome. The cart did not
    // change, so do not treat `itemCount` as an additive bubble delta. Shopify status
    // errors (didError without cartUnchanged) can still add up to the max.
    if (event.detail.data?.cartUnchanged) return;

    const itemCount = event.detail.data?.itemCount ?? event.detail.resource?.item_count ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
